<template>
	<div class="quarterly-wrap" v-loading="quarterlyPerformance.loading">
		<div class="bg-gradient" :style="{ 'min-height': bgGradientMinHeight + 'px' }"></div>
		<div class="quarterly-nav" v-if="dropDownOptions.length">
			<div class="quarterly-tab">
				<div
					:class="['quarterly-tab-item', item.value == quarterActValue && 'tab-active']"
					v-for="(item, index) in quarterTabList[yearActive]"
					:key="index"
					@click="changeQuarter(item)"
				>
					{{ item.label }}
				</div>
			</div>
			<m-inline-dropdown
				:value.sync="yearActive"
				:options="dropDownOptions"
				position="right"
				class="custom-dropdown"
				@change="handleChangeDropDown"
			>
				<template v-slot:title="props">
					<span class="dropdown-menu-text">{{ props.actived.text }}</span>
					<span class="dropdown-angle-icon"></span>
				</template>
			</m-inline-dropdown>
		</div>
		<div class="quarter-main-box">
			<div class="quarter-org">
				{{ OrganizationName }}<span class="code">（{{ code }}）</span>
			</div>
			<div class="quarter-title" :style="computedSize(quarterDetails.announcementTitle)">
				{{ quarterDetails.announcementTitle }}
			</div>
			<div class="quarter-card" v-if="!isSlideTmpl">
				<h4>
					{{ $t('enterprise.quarter-title.financial-indicators')
					}}<span class="more" @click="handleJump"
						>{{ $t('enterprise.more') }}<i class="iconfont icon-flip-right"></i
					></span>
				</h4>
				<div class="quarter-card-box">
					<div class="quarter-card-item" v-for="(value, key) in quarterDetails.coreIndicators" :key="key">
						<div class="quarter-card-data" v-html="handleCoreValue(value, key)"></div>
						<div class="quarter-card-title">{{ core_option[key] }}</div>
					</div>
				</div>
			</div>
			<!-- 业绩路演 -->
			<PerformanceRoadshow v-if="quarterDetails.roadshowId" :quarterDetails="quarterDetails" />
			<!-- 一图读懂 -->
			<OneImageUnderstanding v-if="quarterDetails.longImage" :quarterDetails="quarterDetails" />
			<!-- 业绩材料 -->
			<PerformanceMaterials :quarterDetails="quarterDetails" />
			<!-- 总结展望 -->
			<SummaryOutlook v-if="quarterDetails.summarize" :quarterDetails="quarterDetails" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, inject, nextTick } from 'vue';
import { useCommonData } from '@/hooks';
import { useQuarterlyCommon } from '@rebuild/materials/shared/quarterly_performance/useQuarterlyCommon';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import PerformanceRoadshow from './PerformanceRoadshow.vue';
import OneImageUnderstanding from './OneImageUnderstanding.vue';
import PerformanceMaterials from './PerformanceMaterials.vue';
import SummaryOutlook from './SummaryOutlook.vue';
import { useNavigation } from '@/rebuild/hooks/useNavigation';

const quarterListDetails = ref([]);

const { getOrganizationName, getStockInfoVOS, isSlideTmpl } = useCommonData();
const { core_option, handleCoreValue } = useQuarterlyCommon();
const { proxy } = getCurrentInstance();

const { quarterlyPerformance } = useModuleData();

onMounted(async () => {
	await quarterlyPerformance.refresh();
	quarterListDetails.value = quarterlyPerformance.data?.data;
	initYearQuarter(quarterListDetails.value);

	await nextTick();
	const findWrapperNode = document.querySelector('.mob-module-content-wrapper');
	if (findWrapperNode) {
		bgGradientMinHeight.value = findWrapperNode.offsetHeight;
	}
});
const bgGradientMinHeight = ref(0);

const dropDownOptions = ref([]);

const handleChangeDropDown = () => {
	quarterActValue.value = quarterTabList.value[yearActive.value]?.[0]?.value;
};

const OrganizationName = ref(null);
const code = ref(null);
OrganizationName.value = getOrganizationName.value;
code.value = getStockInfoVOS.value?.[0].code;

const dateArray = ref([]);
const quarterTabList = ref({});
const yearActive = ref();
const quarterActValue = ref();

const quarterLabel = computed(() => ({
	1: proxy.$t('enterprise.first-quarter'),
	2: proxy.$t('enterprise.interim'),
	3: proxy.$t('enterprise.third-quarter'),
	4: proxy.$t('enterprise.annual')
}));

const initYearQuarter = data => {
	for (let key in data) {
		quarterTabList.value[key] = data[key].map(item => ({
			label: quarterLabel.value[item.quarter],
			value: item.quarter
		}));
	}
	dateArray.value = Object.keys(data)
		?.map(year => year)
		?.reverse();
	let target;
	Object.values(data).forEach(item => {
		if (item.find(k => k.isTop)) target = item.find(k => k.isTop);
	});

	yearActive.value = target ? String(target.performanceYear) : dateArray.value?.[0];
	quarterActValue.value = target ? target.quarter : quarterTabList.value[yearActive.value]?.[0]?.value;

	dropDownOptions.value = dateArray.value.map(item => ({ text: item, value: item }));
};

const changeQuarter = item => {
	quarterActValue.value = item.value;
};

const quarterDetails = computed(() => {
	let obj = {};
	const keys = Object.keys(core_option.value).map(key => key);
	let target =
		(quarterListDetails.value?.[yearActive.value] || []).find(item => item.quarter == quarterActValue.value) || {};
	keys.forEach(key => {
		if (Object.prototype.hasOwnProperty.call(target.coreIndicators || {}, key)) {
			obj[key] = target?.coreIndicators?.[key];
		}
	});
	target.coreIndicators = obj;
	return target;
});

const templateMenus = inject('TEMPLATE_MENUS');
const getMenuList = computed(() => templateMenus?.value);
const { goToModule, gotoMenu } = useNavigation();
const handleJump = () => {
	const target = getMenuList.value.find(
		item => item.moduleVOS && item.moduleVOS.find(k => k.moduleId == 'finance_data')
	);
	if (!target?.id) {
		// 跳组件
		goToModule('finance_data');
	} else {
		// 跳菜单
		// 刷随机数，防止缓存命中：fullPath
		gotoMenu(target.id, 'finance_data', { r: new Date().getTime() });
	}
};

const computedSize = val => {
	if (!val) return { 'font-size': '0.22rem', 'line-height': '0.28rem' };
	let length = val.length;
	if (length <= 12) {
		return { 'font-size': '0.22rem', 'line-height': '0.28rem' };
	} else if (length > 12 && length < 16) {
		return { 'font-size': '0.18rem', 'line-height': '0.24rem' };
	} else {
		return { 'font-size': '0.16rem', 'line-height': '0.20rem' };
	}
};
</script>

<style lang="less" scoped>
.quarterly-wrap {
	position: relative;
	z-index: 1;

	.bg-gradient {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: -1;
		background: linear-gradient(201deg, #c3cff3 0.39%, #f5f6f8 37.82%);
		pointer-events: none;
	}

	/deep/.dropdown-nav {
		padding-top: 12px;
	}
	.quarterly-nav {
		padding: 12px 16px 8px;
		display: flex;
		justify-content: space-between;
		background: rgba(255, 255, 255, 0.3);
		height: 44px;
		.quarterly-tab {
			display: flex;
			align-items: center;
			gap: 24px;
			.quarterly-tab-item {
				color: var(--t-secondary-color, #4e5969);
				font-size: 14px;
				font-weight: 400;
				line-height: 18px; /* 128.571% */
			}
			.tab-active {
				position: relative;
				color: var(--t-emphasis-color, #1d2129);
				font-size: 16px;
				font-weight: 500;
			}
		}
		.custom-dropdown {
			/deep/.van-dropdown-item__content {
				max-height: 37%;
			}
			/deep/.dropdown-menu-text {
				color: var(--t-secondary-color, #4e5969);
			}
			.dropdown-angle-icon {
				border-bottom: 4px solid #4e5969;
			}
		}
	}

	.quarter-main-box {
		padding: 32px 16px 0 16px;
		background: url('@/assets/images/quarterly_banner.png') 110% -14px no-repeat;
		background-size: 173px;
		overflow: hidden;

		.quarter-org {
			color: var(--t-emphasis-color, #1d2129);
			font-family: 'Douyin Sans';
			font-size: 26px;
			font-weight: 700;
			.code {
				color: var(--t-emphasis-color, #1d2129);
				font-family: 'DIN Alternate';
				font-size: 20px;
				font-weight: 700;
				letter-spacing: 0.4px;
			}
		}
		.quarter-title {
			padding: 10px 0 28px;
			color: var(--t-emphasis-color, #1d2129);

			font-size: 22px;
			font-weight: 600;
			width: 280px;
		}
		.quarter-card {
			margin-bottom: 12px;
			padding: 16px;
			border-radius: 6px;
			background: #fff;
			h4 {
				display: flex;
				justify-content: space-between;
				align-items: center;
				color: var(--t-emphasis-color, #1d2129);

				font-size: 16px;
				font-weight: 500;
				line-height: 24px; /* 150% */
				.more {
					color: var(--t-secondary-info-color, #86909c);
					font-size: 13px;
					font-weight: 400;
					.icon-flip-righ {
						vertical-align: middle;
					}
				}
			}
			.quarter-card-box {
				padding-top: 12px;
			}
			.quarter-card-item {
				display: flex;
				justify-content: space-between;
				align-items: center;
				&:not(:last-child) {
					padding-bottom: 20px;
				}
				.quarter-card-title {
					padding-bottom: 4px;
					color: var(--t-secondary-info-color, #86909c);

					font-size: 13px;
					font-weight: 400;
				}
				.quarter-card-data {
					color: var(--t-emphasis-color, #1d2129);
					font-family: 'DIN Alternate';
					font-size: 24px;
					font-weight: 700;
				}
			}
		}
	}
}
</style>
